import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Leaf, Sparkles, ShieldCheck, Target, FileText, ArrowRight } from "lucide-react";

const FEATURE_ICONS = [Target, Sparkles, FileText, ShieldCheck];

const FALLBACK = {
  badge: "Harmoni mellan AI-teknik, människa och natur",
  hero_heading: "Samla din kompetens en gång. Skapa rätt CV varje gång.",
  hero_subtitle: "Bygg din Masterprofil och låt AI skapa professionella, ATS-vänliga CV:n skräddarsydda mot varje specifikt jobb – baserat enbart på din verkliga bakgrund.",
  cta_label: "Skapa din profil",
  features: [
    { title: "Matchning mot jobbet", text: "Se stark match, delvis match och luckor innan du skapar CV:t." },
    { title: "AI som aldrig hittar på", text: "AI använder endast din verkliga bakgrund – aldrig påhittade uppgifter." },
    { title: "ATS-vänliga CV:n", text: "Rena, moderna mallar som moderna rekryteringssystem läser korrekt." },
    { title: "Privacy-by-design", text: "Din data är separerad och din. Exportera eller radera när du vill." },
  ],
};

export default function Landing() {
  const [c, setC] = useState(FALLBACK);
  useEffect(() => {
    api.get("/guide-content").then((r) => { if (r.data?.landing) setC(r.data.landing); }).catch(() => {});
  }, []);

  return (
    <div className="hamn-bg min-h-screen">
      <header className="flex items-center justify-between gap-3 px-5 sm:px-6 md:px-12 py-5">
        <div className="flex items-center gap-2.5 min-w-0">
          <img src="/hamn-logo.png" alt="HAMN" className="h-9 w-9 rounded-lg object-cover shrink-0" />
          <span className="font-head font-bold text-primary text-base sm:text-lg whitespace-nowrap">HAMN CV Studio</span>
        </div>
        <div className="flex gap-2 sm:gap-3 shrink-0 items-center">
          <Link to="/foretag/logga-in" className="hidden sm:block"><Button variant="ghost" className="rounded-full text-muted-foreground" data-testid="landing-employer-btn">För arbetsgivare</Button></Link>
          <Link to="/login"><Button variant="ghost" className="rounded-full" data-testid="landing-login-btn">Logga in</Button></Link>
          <Link to="/register"><Button className="rounded-full" data-testid="landing-register-btn">Kom igång</Button></Link>
        </div>
      </header>

      <section className="mx-auto max-w-5xl px-6 pt-16 pb-20 text-center stagger-in">
        <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary">
          <Leaf className="h-4 w-4" /> {c.badge}
        </span>
        <h1 className="font-head text-4xl md:text-6xl font-bold leading-none mt-6 max-w-3xl mx-auto" data-testid="landing-hero-heading">
          {c.hero_heading}
        </h1>
        <p className="text-muted-foreground text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
          {c.hero_subtitle}
        </p>
        <div className="flex justify-center gap-4 mt-9">
          <Link to="/register"><Button size="lg" className="rounded-full h-12 px-8" data-testid="hero-cta-btn">{c.cta_label} <ArrowRight className="h-4 w-4 ml-2" /></Button></Link>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-24 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {(c.features || []).map((f, i) => {
          const Icon = FEATURE_ICONS[i] || Sparkles;
          return (
            <div key={i} className="rounded-2xl border border-border bg-white p-6 transition-transform hover:-translate-y-1">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary mb-4"><Icon className="h-5 w-5" /></div>
              <h3 className="font-head font-semibold text-lg">{f.title}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.text}</p>
            </div>
          );
        })}
      </section>

      <footer className="border-t border-border">
        <div className="mx-auto max-w-6xl px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
          <span>© {new Date().getFullYear()} HAMN CV Studio</span>
          <div className="flex items-center gap-5">
            <Link to="/sa-funkar-det" className="hover:text-foreground" data-testid="landing-guide-link">
              Så funkar det
            </Link>
            <Link to="/integritetspolicy" className="hover:text-foreground" data-testid="landing-policy-link">
              Integritetspolicy
            </Link>
            <Link to="/hjalp" className="hover:text-foreground font-medium text-primary" data-testid="landing-help-link">
              Hjälp & feedback
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
